import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  ClientFilter,
  ExportPdfOptions,
  ExportToExcelOptions,
  Filters,
  LiquidationAggregation,
  MovementExportAdditionalRow,
  MovementExportRow,
  User,
} from "types";
import dayjs from "dayjs";
import { getMovementType, groupBy, groupByMovementType } from "./groupBy";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { arsFormatter, formatDateOnlyDMY, roundToThree } from "helpers";
import { checkSettlementType, checkSettlementTypeWithMinimum } from "./settlements";
import { MovementTypes, Roles, SETTLEMENT_TYPES } from "constants/index";
import { MovementTypeHeaderCell } from "components/utils/LiquidationDetails";
import {
  fceOf,
  liquidationHeader,
  liquidationPalletStorageSubtotal,
  liquidationMovementSettlementHeader,
  liquidationIncomeDischargeUnitsTotal,
  liquidatitonUnitValue,
  liquidationStayUnitsTotal,
  liquidationPalletsUnitsTotal,
  liquidationPalletsSubtotalTotal,
  liquidationStaySubtotalTotal,
  liquidationVolumenUnitsTotal,
  liquidationVolumenSubtotalTotal,
  liquidationSettlementHeader,
  liquidationLogValue,
  buildConsolidatedLiquidation,
  hasSettlementMinimumApplied,
  liquidationArticleVolumeWeight,
  liquidationContainerOf,
  formatLiquidationNo,
} from "./liquidations";

export const buildExcelHeader = (
  clients: ClientFilter[],
  filters: Filters,
  userData: User | null,
) => {
  const now = dayjs().format("DD/MM/YYYY HH:mm");

  const selectedClients = clients.filter((c) =>
    filters.clientIds.includes(c.id),
  );

  const clientsLabel =
    selectedClients.length === 0
      ? userData?.role === Roles.CLIENT
        ? `${userData?.client?.code} - ${userData?.client?.razon_social}`
        : "TODOS"
      : selectedClients.map((c) => `${c.code} - ${c.razon_social}`).join(", ");

  const headerRows = [["Existencias al", now], ["CLIENTES:", clientsLabel], []];

  return { now, clientsLabel, headerRows };
};

export function exportToExcel<T>({
  data,
  columns,
  sheetName,
  fileNamePrefix,
  headerRows = [],
}: ExportToExcelOptions<T>) {
  if (!data || data.length === 0) {
    console.warn("No hay datos para exportar");
    return;
  }

  const rows = data.map((item) => {
    const row: Record<string, any> = {};
    for (const [key, getter] of Object.entries(columns)) {
      row[key] = getter(item);
    }
    return row;
  });

  const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

  const startRow = (headerRows?.length ?? 0) + 2;
  XLSX.utils.sheet_add_json(worksheet, rows, {
    origin: { r: startRow, c: 0 },
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  const date = new Date().toISOString().slice(0, 10);
  saveAs(blob, `${fileNamePrefix}_${date}.xlsx`);
}

const generateLiquidationWorkbook = (liquidation: LiquidationAggregation) => {
  const hasChildLiquidations =
    (liquidation.child_liquidations?.length ?? 0) > 0;

  const headerColumns = [
    "N°",
    "CLIENTE",
    "PERIODO",
    "ALMACEN",
    "VALOR DE RECEPCION",
    "VALOR ESTADIA POR DIA",
    "VALOR PICKING + DESPACHO",
  ];

  const workbook = XLSX.utils.book_new();

  if (!hasChildLiquidations) {
    const rows = [
      headerColumns,
      [
        formatLiquidationNo(liquidation),
        liquidation.liquidation_clients[0].razon_social,
        `${formatDateOnlyDMY(liquidation.date_from.toString())} a ${formatDateOnlyDMY(liquidation.date_to.toString())}`,
        liquidation.store_description,
       checkSettlementTypeWithMinimum(
        liquidation.liquidation_settlement_types[0],
        hasSettlementMinimumApplied(liquidation, 0),
      ),
      checkSettlementTypeWithMinimum(
        liquidation.liquidation_settlement_types[1],
        hasSettlementMinimumApplied(liquidation, 1),
      ),
      checkSettlementTypeWithMinimum(
        liquidation.liquidation_settlement_types[2],
        hasSettlementMinimumApplied(liquidation, 2),
      ),
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cabecera");

    return workbook;
  }

  const parentClientName =
    liquidation.liquidation_clients?.[0]?.razon_social ?? "-";

  const childLiquidations = liquidation.child_liquidations ?? [];

  const rows: (string | number)[][] = [
    [`CLIENTE PADRE: ${parentClientName}`],
    [],
    headerColumns,
    ...childLiquidations.map((currentLiquidation, index) => [
      formatLiquidationNo(currentLiquidation, index),
      currentLiquidation.liquidation_clients?.[0]?.razon_social ?? "-",
      `${formatDateOnlyDMY(currentLiquidation.date_from?.toString() ?? "")} a ${formatDateOnlyDMY(currentLiquidation.date_to?.toString() ?? "")}`,
      currentLiquidation.store_description ?? "-",
      checkSettlementType(
        currentLiquidation.liquidation_settlement_types?.[0],
      ) ?? "-",
      checkSettlementType(
        currentLiquidation.liquidation_settlement_types?.[1],
      ) ?? "-",
      checkSettlementType(
        currentLiquidation.liquidation_settlement_types?.[2],
      ) ?? "-",
    ]),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  worksheet["!merges"] = [
    {
      s: { r: 0, c: 0 },
      e: { r: 0, c: headerColumns.length - 1 },
    },
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, "Cabecera");

  return workbook;
};

const generateLiquidationTotals = (
  liquidation: LiquidationAggregation,
  workbook: XLSX.WorkBook,
) => {
  const childLiquidations = liquidation.child_liquidations ?? [];

  const rows: (string | number)[][] = [
    ["Totales generales"],
    ["Recepción", "Estadías", "Despacho", "Adicionales", "Total"],
    [
      liquidation.liquidation_settlement_types[0].subtotal,
      liquidation.liquidation_settlement_types[1].subtotal,
      liquidation.liquidation_settlement_types[2].subtotal,
      liquidation.subtotal_adicionales,
      liquidation.total,
    ],
  ];

  if (childLiquidations.length > 0) {
    rows.push(
      [],
      ["Totales por línea"],
      ["N°", "Cliente", "Recepción", "Estadías", "Despacho", "Adicionales", "Total"],
      ...childLiquidations.map((child, index) => [
        formatLiquidationNo(child, index),
        child.liquidation_clients?.[0]?.razon_social ?? "-",
        child.liquidation_settlement_types?.[0]?.subtotal ?? 0,
        child.liquidation_settlement_types?.[1]?.subtotal ?? 0,
        child.liquidation_settlement_types?.[2]?.subtotal ?? 0,
        child.subtotal_adicionales ?? 0,
        child.total ?? 0,
      ])
    );
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Totales");
};

export const generateLiquidationIncome = (
  liquidations: LiquidationAggregation[],
  workbook: XLSX.WorkBook,
  hasDetail: boolean,
) => {
  const rows: (string | number | undefined)[][] = [];
  let totalIncomeSubtotal = 0;

  liquidations.forEach((liquidation, liquidationIndex) => {
    const settlementType = liquidation.liquidation_settlement_types[0];
    totalIncomeSubtotal += settlementType.subtotal ?? 0;

    if (liquidationIndex > 0) {
      rows.push([]);
    }

    rows.push([
      `Cliente: ${liquidation.liquidation_clients?.[0]?.razon_social ?? "-"}`,
    ]);

    const clientHeader = hasDetail
      ? ["Tipo Mov.", "Nº Mov", "Doc. Cliente", "Fecha", "Cliente Asoc."].concat(
          liquidationMovementSettlementHeader(settlementType.settlement_type),
        )
      : [
          "Tipo Mov.",
          "Nº Mov",
          "Doc. Cliente",
          "Fecha",
          "Cliente Asoc.",
          "M3",
          "Subtotal",
        ];

    rows.push(clientHeader);

    const movements = liquidation.liquidation_movements
      .filter((m) => m.movement_type_id === MovementTypes.Income)
      .sort((a, b) => b.date.localeCompare(a.date));

    for (let i = 0; i < movements.length; i++) {
      const movement = movements[i];

      const articles = liquidation.liquidation_movement_articles.filter(
        (ma) => ma.movement_id === movement.movement_id,
      );

      if (hasDetail) {
        articles.forEach((movement_article) => {
          const row: (string | number | undefined)[] = [
            MovementTypeHeaderCell(movement.movement_type_id),
            movement.movement_id,
            movement.client_document,
            formatDateOnlyDMY(movement.date),
            movement.associated_client,
          ];

          const article = liquidation.liquidation_articles.find(
            (article) => article.article_id === movement_article.article_id,
          );
          const container = liquidationContainerOf(liquidation, article);
          const isWeightSettlement =
            settlementType.settlement_type === SETTLEMENT_TYPES.PESO ||
            settlementType.settlement_type === SETTLEMENT_TYPES.PESO_VOLUMETRICO;

          row.push(
            article?.code,
            article?.description,
            movement_article.amount,
          );

          if (isWeightSettlement) {
            const articleWeight = Number(article?.weight ?? 0);
            const articleVolumeWeight = liquidationArticleVolumeWeight(article, container);
            const selectedWeightValue =
              Number(movement_article.amount ?? 0) > 0
                ? Number(movement_article.value ?? 0) / Number(movement_article.amount)
                : Math.max(articleWeight, articleVolumeWeight);

            row.push(roundToThree(articleWeight));
            row.push(roundToThree(articleVolumeWeight));
            row.push(roundToThree(selectedWeightValue));
            row.push(roundToThree(movement_article.value));
          }

          if (settlementType.settlement_type !== SETTLEMENT_TYPES.ABONO) {
            const isUnits = settlementType.settlement_type === SETTLEMENT_TYPES.UNIDADES;

            if (!isUnits && !isWeightSettlement) {
              row.push(
                liquidatitonUnitValue(
                  settlementType.settlement_type,
                  liquidation,
                  movement_article,
                ),
              );
              row.push(roundToThree(movement_article.value));
            }

            row.push(arsFormatter(settlementType.calculation));
            row.push(arsFormatter(movement_article.subtotal));
          }

          rows.push(row);
        });
      } else {
        const unitsTotal = liquidationIncomeDischargeUnitsTotal(
          settlementType,
          movement,
          articles,
          liquidation,
        );

        rows.push([
          MovementTypeHeaderCell(movement.movement_type_id),
          movement.movement_id,
          movement.client_document,
          formatDateOnlyDMY(movement.date),
          movement.associated_client,
          roundToThree(unitsTotal || 0),
          arsFormatter(movement.subtotal),
        ]);
      }
    }
  });

  if (!hasDetail) {
    rows.push([]);
    rows.push([
      "Total Recepción",
      "",
      "",
      "",
      "",
      "",
      arsFormatter(totalIncomeSubtotal),
    ]);
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Recepciones");
};

export const generateLiquidationDischarge = (
  liquidations: LiquidationAggregation[],
  workbook: XLSX.WorkBook,
  hasDetail: boolean,
) => {
  const rows: (string | number | undefined)[][] = [];
  let totalDischargeSubtotal = 0;

  liquidations.forEach((liquidation, liquidationIndex) => {
    const settlementType = liquidation.liquidation_settlement_types[2];
    totalDischargeSubtotal += settlementType.subtotal ?? 0;

    if (liquidationIndex > 0) {
      rows.push([]);
    }

    rows.push([
      `Cliente: ${liquidation.liquidation_clients?.[0]?.razon_social ?? "-"}`,
    ]);

    const clientHeader = hasDetail
      ? ["Tipo Mov.", "Nº Mov", "Doc. Cliente", "Fecha", "Cliente Asoc."].concat(
          liquidationMovementSettlementHeader(settlementType.settlement_type),
        )
      : [
          "Tipo Mov.",
          "Nº Mov",
          "Doc. Cliente",
          "Fecha",
          "Cliente Asoc.",
          "M3",
          "Subtotal",
        ];

    rows.push(clientHeader);

    const movements = liquidation.liquidation_movements
      .filter((m) => m.movement_type_id === MovementTypes.Discharge)
      .sort((a, b) => b.date.localeCompare(a.date));

    for (let i = 0; i < movements.length; i++) {
      const movement = movements[i];

      const articles = liquidation.liquidation_movement_articles.filter(
        (ma) => ma.movement_id === movement.movement_id,
      );

      if (hasDetail) {
        articles.forEach((movement_article) => {
          const row: (string | number | undefined)[] = [
            MovementTypeHeaderCell(movement.movement_type_id),
            movement.movement_id,
            movement.client_document,
            formatDateOnlyDMY(movement.date),
            movement.associated_client,
          ];

          const article = liquidation.liquidation_articles.find(
            (article) => article.article_id === movement_article.article_id,
          );
          const container = liquidationContainerOf(liquidation, article);
          const isWeightSettlement =
            settlementType.settlement_type === SETTLEMENT_TYPES.PESO ||
            settlementType.settlement_type === SETTLEMENT_TYPES.PESO_VOLUMETRICO;

          row.push(
            article?.code,
            article?.description,
            movement_article.amount,
          );

          if (isWeightSettlement) {
            const articleWeight = Number(article?.weight ?? 0);
            const articleVolumeWeight = liquidationArticleVolumeWeight(article, container);
            const selectedWeightValue =
              Number(movement_article.amount ?? 0) > 0
                ? Number(movement_article.value ?? 0) / Number(movement_article.amount)
                : Math.max(articleWeight, articleVolumeWeight);

            row.push(roundToThree(articleWeight));
            row.push(roundToThree(articleVolumeWeight));
            row.push(roundToThree(selectedWeightValue));
            row.push(roundToThree(movement_article.value));
          }

          if (settlementType.settlement_type !== SETTLEMENT_TYPES.ABONO) {
            const isUnits = settlementType.settlement_type === SETTLEMENT_TYPES.UNIDADES;

            if (!isUnits && !isWeightSettlement) {
              row.push(
                liquidatitonUnitValue(
                  settlementType.settlement_type,
                  liquidation,
                  movement_article,
                ),
              );
              row.push(roundToThree(movement_article.value));
            }

            row.push(arsFormatter(settlementType.calculation));
            row.push(arsFormatter(movement_article.subtotal));
          }

          rows.push(row);
        });
      } else {
        const unitsTotal = liquidationIncomeDischargeUnitsTotal(
          settlementType,
          movement,
          articles,
          liquidation,
        );

        rows.push([
          MovementTypeHeaderCell(movement.movement_type_id),
          movement.movement_id,
          movement.client_document,
          formatDateOnlyDMY(movement.date),
          movement.associated_client,
          roundToThree(unitsTotal || 0),
          arsFormatter(movement.subtotal),
        ]);
      }
    }
  });

  if (!hasDetail) {
    rows.push([]);
    rows.push([
      "Total Picking y Despacho",
      "",
      "",
      "",
      "",
      "",
      arsFormatter(totalDischargeSubtotal),
    ]);
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Picking y Despacho");
};

export const generateLiquidationStay = (
  liquidations: LiquidationAggregation[],
  workbook: XLSX.WorkBook,
  hasDetail: boolean,
) => {
  const rows: (string | number | undefined)[][] = [];
  let totalStaySubtotal = 0;

  liquidations.forEach((liquidation, liquidationIndex) => {
    const settlementType = liquidation.liquidation_settlement_types[1];
    totalStaySubtotal += settlementType.subtotal ?? 0;

    if (liquidationIndex > 0) {
      rows.push([]);
    }

    rows.push([
      `Cliente: ${liquidation.liquidation_clients?.[0]?.razon_social ?? "-"}`,
    ]);

    const clientHeader = hasDetail
      ? ["Fecha"].concat(
          liquidationSettlementHeader(settlementType.settlement_type),
        )
      : (() => {
          const header: string[] = ["Fecha"];

          if (settlementType.settlement_type !== SETTLEMENT_TYPES.ABONO) {
            header.push(liquidationHeader(settlementType.settlement_type));
          }

          header.push("Subtotal");
          return header;
        })();

    rows.push(clientHeader);

    const logsGroupedByDay = Object.entries(
      groupBy(liquidation.liquidation_article_location_log, ["date"]),
    ).sort((a, b) => b[0].localeCompare(a[0]));

    for (let i = 0; i < logsGroupedByDay.length; i++) {
      const log = logsGroupedByDay[i];
      const date = log[0];
      const logsOfDay = log[1];

      if (hasDetail) {
        logsOfDay.forEach((log) => {
          const article = liquidation.liquidation_articles.find(
            (a) => a.article_id === log.article_id,
          );
          const unitsPerPallet = article?.units_per_pallet ?? 0;

          const container = liquidationContainerOf(liquidation, article, log);
          const articleWeight = Number(log.physical_weight ?? article?.weight ?? 0);
          const articleVolumeWeight = log.volume_weight ?? liquidationArticleVolumeWeight(article, container);
          const selectedWeightValue =
            Math.max(articleWeight, articleVolumeWeight);
          const selectedWeightTotal = Number(log.quantity ?? 0) * selectedWeightValue;
          const isWeightSettlement =
            settlementType.settlement_type === SETTLEMENT_TYPES.PESO ||
            settlementType.settlement_type === SETTLEMENT_TYPES.PESO_VOLUMETRICO;

          const logDetail: (string | number | undefined)[] = [
            formatDateOnlyDMY(date),
            liquidation.liquidation_articles.find(
              (article) => article.article_id === log.article_id,
            )?.code,
            liquidation.liquidation_articles.find(
              (article) => article.article_id === log.article_id,
            )?.description,
            log.quantity,
          ];

          if (isWeightSettlement) {
            logDetail.push(roundToThree(articleWeight));
            logDetail.push(roundToThree(articleVolumeWeight));
            logDetail.push(roundToThree(selectedWeightValue));
            logDetail.push(roundToThree(selectedWeightTotal));
            logDetail.push(arsFormatter(settlementType.calculation));
            logDetail.push(arsFormatter(selectedWeightTotal * settlementType.calculation));
          }

          if (
            settlementType.settlement_type !== SETTLEMENT_TYPES.ABONO &&
            settlementType.settlement_type !== SETTLEMENT_TYPES.PALLETS &&
            settlementType.settlement_type !== SETTLEMENT_TYPES.VOLUMEN &&
            settlementType.settlement_type !== SETTLEMENT_TYPES.UNIDADES && 
            !isWeightSettlement
          ) {
            logDetail.push(
              liquidatitonUnitValue(
                settlementType.settlement_type,
                liquidation,
                undefined,
                log,
              ),
            );
            const logValue = liquidationLogValue(
              liquidation,
              settlementType,
              [log],
              log.article_id,
            );
            logDetail.push(roundToThree(logValue));
            logDetail.push(arsFormatter(settlementType.calculation));
            logDetail.push(arsFormatter(logValue * settlementType.calculation));
          }

          if (settlementType.settlement_type === SETTLEMENT_TYPES.PALLETS) {
            const totalPallets =
              unitsPerPallet > 0 ? log.quantity / unitsPerPallet : 0;
            const fce = fceOf(liquidation, log.article_id);
            logDetail.push(roundToThree(unitsPerPallet));
            logDetail.push(roundToThree(totalPallets));
            logDetail.push(arsFormatter(settlementType.calculation));
            logDetail.push(roundToThree(fce));
            logDetail.push(
              arsFormatter(
                liquidationPalletStorageSubtotal(
                  settlementType,
                  liquidation,
                  log,
                ),
              ),
            );
          }

          if (settlementType.settlement_type === SETTLEMENT_TYPES.VOLUMEN) {
            const logValue = liquidationLogValue(
              liquidation,
              settlementType,
              [log],
              log.article_id,
            );

            logDetail.push(
              liquidatitonUnitValue(
                settlementType.settlement_type,
                liquidation,
                undefined,
                log,
              ),
            );
            logDetail.push(roundToThree(logValue));
            logDetail.push(arsFormatter(settlementType.calculation));
            logDetail.push(arsFormatter(logValue * settlementType.calculation));
          }

          if (settlementType.settlement_type === SETTLEMENT_TYPES.UNIDADES) {
            logDetail.push(arsFormatter(settlementType.calculation));
            logDetail.push(
              arsFormatter(Number(log.quantity ?? 0) * settlementType.calculation),
            );
          }

          rows.push(logDetail);
        });
      } else {
        const unitsTotal = liquidationStayUnitsTotal(
          settlementType,
          liquidation,
          logsOfDay,
        );

        const palletsUnitsTotal = liquidationPalletsUnitsTotal(
          settlementType,
          liquidation,
          logsOfDay,
        );

        const palletsSubtotalTotal = liquidationPalletsSubtotalTotal(
          settlementType,
          liquidation,
          logsOfDay,
        );

        const volumenUnitsTotal = liquidationVolumenUnitsTotal(
          settlementType,
          liquidation,
          logsOfDay,
        );

        const volumenSubtotalTotal = liquidationVolumenSubtotalTotal(
          settlementType,
          volumenUnitsTotal,
        );

        const logRow: (string | number)[] = [formatDateOnlyDMY(date)];

        if (settlementType.settlement_type !== SETTLEMENT_TYPES.ABONO) {
          logRow.push(
            roundToThree(
              settlementType.settlement_type === SETTLEMENT_TYPES.PALLETS
                ? palletsUnitsTotal
                : settlementType.settlement_type === SETTLEMENT_TYPES.VOLUMEN
                  ? volumenUnitsTotal
                  : unitsTotal || 0,
            ),
          );
        }

        logRow.push(
          arsFormatter(
            liquidationStaySubtotalTotal(
              settlementType,
              liquidation,
              logsOfDay,
            ),
          ),
        );

        rows.push(logRow);
      }
    }
  });

  if (!hasDetail) {
    rows.push([]);
    rows.push(["Total Estadías", "", arsFormatter(totalStaySubtotal)]);
  }

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Estadias");
};

export const generateLiquidationAdditionals = (
  liquidations: LiquidationAggregation[],
  workbook: XLSX.WorkBook,
  hasDetail: boolean,
) => {
  const headerRows = [
    hasDetail
      ? [
          "Tipo Mov.",
          "Nº Mov",
          "Doc. Cliente",
          "Fecha",
          "Cliente Asoc.",
          "Descripción",
          "Cantidad (Unidades)",
          "Precio",
          "Subtotal",
        ]
      : [
          "Tipo Mov.",
          "Nº Mov",
          "Doc. Cliente",
          "Fecha",
          "Cliente Asoc.",
          "Subtotal",
        ],
  ];

  const rows: (string | number | undefined)[][] = [];
  let totalAdditionalsSubtotal = 0;

  liquidations.forEach((liquidation, liquidationIndex) => {
    totalAdditionalsSubtotal += liquidation.subtotal_adicionales ?? 0;

    if (liquidationIndex > 0) {
      rows.push([]);
    }

    rows.push([
      `Cliente: ${liquidation.liquidation_clients?.[0]?.razon_social ?? "-"}`,
    ]);

    const incomeAndDischargesWithAdditionals = new Set();
    liquidation.liquidation_movement_additional_items.forEach((m) =>
      incomeAndDischargesWithAdditionals.add(m.movement_id),
    );

    const movements = liquidation.liquidation_movements.filter(
      (m) =>
        m.movement_type_id === MovementTypes.Additional ||
        incomeAndDischargesWithAdditionals.has(m.movement_id),
    );

    for (let i = 0; i < movements.length; i++) {
      const movement = movements[i];

      const additional_items =
        liquidation.liquidation_movement_additional_items.filter(
          (mai) => mai.movement_id === movement.movement_id,
        );

      if (hasDetail) {
        additional_items.forEach((movement_additional_items) => {
          rows.push([
            MovementTypeHeaderCell(movement.movement_type_id),
            movement.movement_id,
            movement.client_document,
            formatDateOnlyDMY(movement.date),
            movement.associated_client,
            liquidation.liquidation_additional_items.find(
              (additional_item) =>
                additional_item.additional_item_id ===
                movement_additional_items.additional_item_id,
            )?.description,
            movement_additional_items.amount,
            arsFormatter(
              liquidation.liquidation_additional_items.find(
                (additional_item) =>
                  additional_item.additional_item_id ===
                  movement_additional_items.additional_item_id,
              )?.price || 0,
            ),
            arsFormatter(movement_additional_items.subtotal),
          ]);
        });
      } else {
        let sum = 0;
        additional_items.forEach((ai) => (sum += ai.subtotal));

        rows.push([
          MovementTypeHeaderCell(movement.movement_type_id),
          movement.movement_id,
          movement.client_document,
          formatDateOnlyDMY(movement.date),
          movement.associated_client,
          arsFormatter(sum),
        ]);
      }
    }
  });

  if (!hasDetail) {
    rows.push([]);
    rows.push([
      "Total Adicionales",
      "",
      "",
      "",
      "",
      arsFormatter(totalAdditionalsSubtotal),
    ]);
  }

  const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...rows]);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Adicionales");
};

const sanitizeFileNamePart = (value: string, maxLength = 100) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);

const buildLiquidationFileName = (liquidation: LiquidationAggregation) => {
  const childLiquidations = liquidation.child_liquidations ?? [];
  const parentClientName =
    liquidation.liquidation_clients?.[0]?.razon_social ?? "";
  const clientNames = childLiquidations.length
    ? [
        parentClientName,
        ...childLiquidations.map(
          (child) => child.liquidation_clients?.[0]?.razon_social ?? ""
        ),
      ]
    : [parentClientName];
  const clientLabel = clientNames.join("_");
  const liquidationNo = formatLiquidationNo(liquidation);
  const prefix = `Liquidacion_${liquidationNo}_`;
  const extension = ".xlsx";
  const maxClientLabelLength = Math.max(
    0,
    100 - prefix.length - extension.length
  );
  const safeClientLabel = sanitizeFileNamePart(
    clientLabel,
    maxClientLabelLength
  );
  return safeClientLabel
    ? `${prefix}${safeClientLabel}${extension}`
    : `Liquidacion_${liquidationNo}${extension}`;
};

export const exportLiquidationToExcel = (
  liquidation: LiquidationAggregation,
  hasDetail: boolean = true,
) => {
  const consolidatedLiquidation = buildConsolidatedLiquidation(liquidation);
  const liquidationsToExport = getLiquidationsToExport(liquidation);

  const workbook = generateLiquidationWorkbook(consolidatedLiquidation);

  generateLiquidationTotals(consolidatedLiquidation, workbook);
  generateLiquidationIncome(liquidationsToExport, workbook, hasDetail);
  generateLiquidationStay(liquidationsToExport, workbook, hasDetail);
  generateLiquidationDischarge(liquidationsToExport, workbook, hasDetail);
  generateLiquidationAdditionals(liquidationsToExport, workbook, hasDetail);

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, buildLiquidationFileName(liquidation));
};

const appendMovementAdditionalItemsSheet = (
  workbook: XLSX.WorkBook,
  additionalItems: MovementExportAdditionalRow[],
) => {
  const headerRows = [
    [
      "N° MOVIMIENTO PRINCIPAL",
      "TIPO MOVIMIENTO PRINCIPAL",
      "N° MOVIMIENTO ADICIONAL",
      "FECHA",
      "DOC. CLIENTE",
      "CLIENTE ASOCIADO",
      "DESCRIPCIÓN",
      "CANTIDAD",
      "PRECIO",
      "SUBTOTAL",
    ],
  ];
  const rows = additionalItems.map((item) => [
    item.parent_movement_id ?? "-",
    item.parent_movement_type_id == null
      ? "-"
      : getMovementType(item.parent_movement_type_id),
    item.additional_movement_id ?? "-",
    dayjs(item.date).format("DD/MM/YYYY"),
    item.client_document || "-",
    item.associated_client || "-",
    item.description,
    Number(item.amount ?? 0),
    Number(item.price ?? 0),
    Number(item.subtotal ?? 0),
  ]);
  const total = additionalItems.reduce(
    (sum, item) => sum + Number(item.subtotal ?? 0),
    0,
  );
  const worksheet = XLSX.utils.aoa_to_sheet([
    ...headerRows,
    ...rows,
    [],
    ["TOTAL ADICIONALES", "", "", "", "", "", "", "", "", total],
  ]);

  XLSX.utils.book_append_sheet(workbook, worksheet, "Adicionales");
};

export const exportMovementsToExcel = (
  movements: MovementExportRow[],
  additionalItems: MovementExportAdditionalRow[],
  fromDate: string,
  toDate: string,
  clientLabel: string,
) => {
  if (
    (!movements || movements.length === 0) &&
    (!additionalItems || additionalItems.length === 0)
  ) {
    console.warn("No hay movimientos para exportar");
    return;
  }

  const mainMovements = movements.filter(
    (movement) => movement.movement_type_id !== MovementTypes.Additional,
  );

  const headerRows = [
    [
      fromDate && toDate && dayjs(fromDate).isValid() && dayjs(toDate).isValid()
        ? `Listado de movimientos desde ${dayjs(fromDate).format("DD/MM/YYYY")} hasta ${dayjs(toDate).format("DD/MM/YYYY")}`
        : "Listado de todos los movimientos",
    ],
    [`Cliente: ${clientLabel}`],
    [],
    [
      "TIPO",
      "N° MOVIMIENTO",
      "FECHA",
      "CLIENTE",
      "CLIENTE ASOCIADO",
      "DOC CLIENTE",
      "REMITO",
      "PALLETS",
      "UNIDADES",
      "PESO (KG)",
      "OBSERVACIONES",
      "DEPÓSITO",
      "ADICIONAL",
    ],
  ];

  const getMovementArticlesAmount = (movement: MovementExportRow) =>
    Number(movement.articles_amount ?? 0);

  const rows = mainMovements.map((mov) => [
    getMovementType(mov.movement_type_id),
    mov.id,
    dayjs(mov.date).format("DD/MM/YYYY"),
    mov.client?.razon_social || "-",
    mov.associated_client || "-",
    mov.client_document || "-",
    mov.numero_remito || "-",
    mov.pallets || 0,
    getMovementArticlesAmount(mov),
    mov.weight || 0,
    mov.observation || "-",
    mov.store_id || "-",
    mov.has_additional_movements ? "SI" : "NO",
  ]);

  const grouped = groupByMovementType(mainMovements);
  const subtotalRows: any[] = [];

  Object.entries(grouped).forEach(([type, items]) => {
    const subtotalPallets = items.reduce(
      (acc, m) => acc + (m.pallets || 0),
      0,
    );
    const subtotalWeight = items.reduce(
      (acc, m) => acc + (m.weight || 0),
      0,
    );
    const subtotalArticles = items.reduce(
      (acc, m) => acc + getMovementArticlesAmount(m),
      0,
    );

    const count = items.length;

    subtotalRows.push([
      `SUBTOTAL ${type.toUpperCase()}`,
      `${count} ${type.toLowerCase()}${count > 1 ? "s" : ""}`,
      "",
      "",
      "",
      "",
      "",
      subtotalPallets,
      subtotalArticles,
      subtotalWeight,
      "",
      "",
      "",
    ]);
  });

  const totalPallets = mainMovements.reduce(
    (acc, m) => acc + (m.pallets || 0),
    0,
  );
  const totalWeight = mainMovements.reduce(
    (acc, m) => acc + (m.weight || 0),
    0,
  );
  const totalArticles = mainMovements.reduce(
    (acc, m) => acc + getMovementArticlesAmount(m),
    0,
  );
  const totalCount = mainMovements.length;

  subtotalRows.push([]);
  subtotalRows.push([
    "TOTAL MOVIMIENTOS",
    `${totalCount} movimiento${totalCount > 1 ? "s" : ""}`,
    "",
    "",
    "",
    "",
    "",
    totalPallets,
    totalArticles,
    totalWeight,
    "",
    "",
    "",
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([
    ...headerRows,
    ...rows,
    [],
    ...subtotalRows,
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Movimientos");
  appendMovementAdditionalItemsSheet(workbook, additionalItems);

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  const date = new Date().toISOString().slice(0, 10);
  saveAs(blob, `Movimientos_${clientLabel}_${date}.xlsx`);
};

export function exportTableToPdf<T>({
  data,
  columns,
  title,
  subtitle,
  fileNamePrefix,
  tableFontSize,
  tableCellPadding,
}: ExportPdfOptions<T>) {
  if (!data || data.length === 0) return;

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const now = dayjs().format("DD/MM/YYYY HH:mm");

  doc.setFontSize(14);
  doc.text(title, 14, 15);

  doc.setFontSize(9);
  doc.text(`Fecha: ${now}`, 14, 22);
  if (subtitle) doc.text(subtitle, 14, 27);

  const columnStyles = columns.reduce<Record<number, Record<string, unknown>>>(
    (styles, column, index) => {
      if (column.pdfCellWidth === undefined && column.pdfMinCellWidth === undefined) {
        return styles;
      }

      styles[index] = {
        ...(column.pdfCellWidth !== undefined
          ? { cellWidth: column.pdfCellWidth }
          : {}),
        ...(column.pdfMinCellWidth !== undefined
          ? { minCellWidth: column.pdfMinCellWidth }
          : {}),
      };

      return styles;
    },
    {},
  );

  autoTable(doc, {
    startY: subtitle ? 35 : 30,
    head: [columns.map((c) => c.header)],
    body: data.map((row) => columns.map((c) => c.value(row))),
    styles: {
      fontSize: tableFontSize ?? 8,
      cellPadding: tableCellPadding ?? 2,
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: 0,
      fontStyle: "bold",
    },
    columnStyles,
    didDrawPage: () => {
      const pageNumber = doc.getNumberOfPages();
      const pageSize = doc.internal.pageSize;

      doc.setFontSize(8);
      doc.text(
        `Página ${pageNumber}`,
        pageSize.getWidth() - 20,
        pageSize.getHeight() - 10,
        { align: "right" },
      );
    },
  });

  doc.save(`${fileNamePrefix}_${dayjs().format("YYYY-MM-DD")}.pdf`);
}

export const getLiquidationsToExport = (
  liquidation: LiquidationAggregation,
) => {
  const hasChildLiquidations =
    (liquidation.child_liquidations?.length ?? 0) > 0;

  if (hasChildLiquidations) {
    return liquidation.child_liquidations ?? [];
  }

  return [liquidation];
};
